import { Board, Template } from 'modules/core/domain/interfaces/supabase/overlay/entity';
import supabase from 'modules/core/supabase/supabase';
import { TokenStorage } from 'modules/core/token-storage';
import { syncGameInfo } from 'modules/overlay-v2/store/game-info/actions';
import { setPassedEventsId as setPassedEventsIdGameInfo } from 'modules/overlay-v2/store/game-info/game-info';
import {
  setBoardLocked,
  setHideControlPanel,
  setIsMatchDataReady,
  setPassedEventsId as setPassedEventsIdOverlay,
} from 'modules/overlay-v2/store/overlay/overlay';
import { selectBroadCastState } from 'modules/overlay-v2/store/overlay/selectors';
import { syncTime } from 'modules/overlay-v2/store/time/actions';
import useGameInfo from 'modules/overlay-v2/ui/hooks/use-game-info';
import { getCurrentBoard } from 'modules/stream/domain/utils/get-current-board';
import { fetchTemplateWithBoards } from 'modules/template-editor/store/templates/actions';
import { setActiveBoard, setActiveTemplate } from 'modules/template-editor/store/templates/templates';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useQueryParam } from 'use-query-params';

function useInitDataOverlayV2() {
  const dispatch = useAppDispatch();

  const { organizerId } = useParams();
  const tournamentId = Number(organizerId ?? '');
  if (isNaN(tournamentId)) throw new Error('Tournament id is invalid', { cause: 'organizerId is not a number' });
  supabase.overlayLogs.init({ tournamentId });

  // init token
  const encodeToken = useQueryParam('token')[0] as string | undefined;
  if (!encodeToken) throw new Error('Token is required', { cause: 'queryParam token is undefined' });
  // const token = decodeToken(encodeToken);
  // if (!token) throw new Error('Token is invalid', { cause: 'decodeToken returned undefined' });
  useEffect(() => {
    const tokenStorage = new TokenStorage();
    tokenStorage.saveToken(encodeToken);
  }, [encodeToken]);

  const boardId = useQueryParam('boardId')[0] as string | undefined;
  const isLocked = useQueryParam('locked')[0] as string | undefined;
  const isHidePanel = useQueryParam('hidePanel')[0] as string | undefined;

  useEffect(() => {
    dispatch(setActiveBoard({ id: boardId ? parseInt(boardId) : undefined }));
    dispatch(setBoardLocked(isLocked === 'true'));
    dispatch(setHideControlPanel(isHidePanel === 'true'));
  }, [boardId, dispatch, isHidePanel, isLocked]);

  const { matchId } = useAppSelector(selectBroadCastState);
  useEffect(() => {
    if (!matchId) return;

    dispatch(setIsMatchDataReady(false));
    dispatch(setPassedEventsIdGameInfo([]));
    dispatch(setPassedEventsIdOverlay([]));

    supabase.overlayLogs.init({ matchId });

    let cancelled = false;
    (async () => {
      try {
        await dispatch(syncGameInfo({ matchId })).unwrap();
        const data = await supabase.matchChronology.getRow(matchId);
        const passedEvents = data?.chronology.map((event) => event.id) ?? [];
        if (cancelled) return;
        dispatch(setPassedEventsIdGameInfo(passedEvents));
        dispatch(setPassedEventsIdOverlay(passedEvents));
        await dispatch(syncTime({ matchId })).unwrap();
        if (cancelled) return;
        dispatch(setIsMatchDataReady(true));
      } catch (error) {
        if (cancelled) return;
        if (error instanceof Error) {
          supabase.overlayLogs.createError({
            comment: 'Не удалось сделать первоначальную настройку',
            message: JSON.stringify({ message: error.message, stack: error.stack }),
          });
        }
        dispatch(setIsMatchDataReady(true));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, matchId]);

  const { championship } = useGameInfo();
  useEffect(() => {
    if (!championship?.overlayId) return;

    dispatch(fetchTemplateWithBoards({ templateId: championship.overlayId }))
      .then(({ payload }) => {
        const { template } = payload as { template: Template; boards: Board[] };
        dispatch(setActiveTemplate({ id: template.id }));
        window?.opener?.postMessage(JSON.stringify({ type: 'popupLoaded' }), '*');
      })
      .catch((data) => {
        supabase.overlayLogs.createError({
          comment: 'Не удалось получить шаблоны пользователя',
          message: data.toString(),
        });
      });

    supabase.overlayLogs.init({
      championshipId: championship.id,
      templateId: championship.overlayId,
    });
  }, [championship?.id, championship?.overlayId, dispatch]);

  const { isLoaded } = useTemplates();
  useEffect(() => {
    if (!matchId) return;
    if (!isLoaded) return;
    if (boardId) return;
    getCurrentBoard(matchId).then((currentBoard) => {
      dispatch(setActiveBoard({ id: currentBoard }));
    });
  }, [dispatch, matchId, isLoaded, boardId]);
}

export default useInitDataOverlayV2;
